import { useEffect, useRef } from 'react'
import QRCodeStyling from 'qr-code-styling'
import { Box } from '@mui/material'

interface ThemedQRCodeProps {
  data: string
  size?: number
}

export function ThemedQRCode({ data, size = 300 }: ThemedQRCodeProps) {
  const qrRef = useRef<HTMLDivElement>(null)
  const qrCodeRef = useRef<QRCodeStyling | null>(null)

  useEffect(() => {
    if (!qrRef.current) return

    // Create the HA logo as SVG
    const logoSvg = `
      <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
        <!-- Orange/brown background with rounded corners -->
        <rect width="80" height="80" rx="12" fill="#b8762e"/>
        <!-- HA text in white -->
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="36" font-weight="bold"
              fill="white" text-anchor="middle" dominant-baseline="middle">HA</text>
      </svg>
    `
    const logoDataUrl = `data:image/svg+xml;base64,${btoa(logoSvg)}`

    qrCodeRef.current = new QRCodeStyling({
      width: size,
      height: size,
      data: data,
      margin: 10,
      qrOptions: {
        typeNumber: 0,
        mode: 'Byte',
        errorCorrectionLevel: 'H', // High error correction to allow logo in center
      },
      imageOptions: {
        hideBackgroundDots: true,
        imageSize: 0.3,
        margin: 4,
      },
      dotsOptions: {
        type: 'rounded',
        color: '#2d5016', // Dark green matching your brand
      },
      backgroundOptions: {
        color: '#f5f1e8', // Beige/cream background like in examples
      },
      cornersSquareOptions: {
        type: 'extra-rounded',
        color: '#2d5016', // Dark green
      },
      cornersDotOptions: {
        type: 'dot',
        color: '#2d5016',
      },
      image: logoDataUrl,
    })

    // Clear previous content
    if (qrRef.current) {
      qrRef.current.innerHTML = ''
      qrCodeRef.current.append(qrRef.current)
    }

    // Cleanup
    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = ''
      }
    }
  }, [data, size])

  return (
    <Box
      ref={qrRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& canvas': {
          borderRadius: 2,
          boxShadow: 1,
        },
      }}
    />
  )
}
